import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Send, Image as ImageIcon, RefreshCw, Check, X, AlertCircle, Loader2,
  Radio, ArrowLeft, History, Users,
} from "lucide-react";

const formatDate = (d) => d ? new Date(d).toLocaleString("es-CO", {
  day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "-";

/**
 * AdminAutoPostBroadcast — Simplified flow:
 * 1) Mensaje (caption)
 * 2) Seleccionar modelos (checklist)
 * 3) Click "Asignar media" → modal con bóveda
 * 4) Enviar
 *
 * Backend resolves each selected model → all their configured channels.
 */
export default function AdminAutoPostBroadcast() {
  const { getAuthHeaders } = useAuth();

  const [caption, setCaption] = useState("");
  const [stripLinks, setStripLinks] = useState(false);
  const [tierFilter, setTierFilter] = useState("both"); // 'free'|'vip'|'both'

  const [models, setModels] = useState([]);
  const [selectedModels, setSelectedModels] = useState(new Set());
  const [loadingModels, setLoadingModels] = useState(false);

  // Media modal
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [mediaModalTarget, setMediaModalTarget] = useState(null); // username being assigned media for
  const [media, setMedia] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [mediaFilterCreator, setMediaFilterCreator] = useState("");
  const [mediaFilterType, setMediaFilterType] = useState("photo");
  // Per-model media assignment: { username: mediaObj }
  const [assignments, setAssignments] = useState({});

  const [history, setHistory] = useState([]);
  const [activeBroadcastId, setActiveBroadcastId] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  // ===== Load models =====
  const loadModels = useCallback(async () => {
    setLoadingModels(true);
    try {
      const r = await fetch("/api/admin/autopost/broadcast/creators-summary", { headers: getAuthHeaders() });
      const d = await r.json();
      setModels(d.items || []);
    } catch {
      toast.error("No se pudo cargar las modelos");
    } finally {
      setLoadingModels(false);
    }
  }, [getAuthHeaders]);

  const loadMedia = useCallback(async () => {
    setLoadingMedia(true);
    try {
      const params = new URLSearchParams();
      if (mediaFilterCreator) params.set("creator", mediaFilterCreator);
      if (mediaFilterType) params.set("media_type", mediaFilterType);
      const r = await fetch(`/api/admin/autopost/broadcast/media-library?${params}`, { headers: getAuthHeaders() });
      const d = await r.json();
      setMedia(d.items || []);
    } catch {
      toast.error("No se pudo cargar la bóveda");
    } finally {
      setLoadingMedia(false);
    }
  }, [getAuthHeaders, mediaFilterCreator, mediaFilterType]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await fetch("/api/admin/autopost/broadcast/history?limit=20", { headers: getAuthHeaders() });
      const d = await r.json();
      setHistory(d.items || []);
    } catch { /* silent */ }
  }, [getAuthHeaders]);

  useEffect(() => { loadModels(); loadHistory(); }, [loadModels, loadHistory]);
  useEffect(() => { if (mediaModalOpen) loadMedia(); }, [mediaModalOpen, loadMedia]);

  // Poll active broadcast
  useEffect(() => {
    if (!activeBroadcastId) return;
    const id = setInterval(async () => {
      try {
        const r = await fetch(`/api/admin/autopost/broadcast/history/${activeBroadcastId}`, { headers: getAuthHeaders() });
        if (!r.ok) return;
        const d = await r.json();
        setHistory(prev => {
          const next = prev.map(p => p.id === d.id ? { ...p, ...d } : p);
          if (!next.find(p => p.id === d.id)) next.unshift(d);
          return next;
        });
        if (d.status === "done") {
          setActiveBroadcastId(null);
          toast.success(`Broadcast: ${d.sentCount} ok / ${d.failedCount} fallaron`);
        }
      } catch { /* silent */ }
    }, 2500);
    return () => clearInterval(id);
  }, [activeBroadcastId, getAuthHeaders]);

  // ===== Helpers =====
  const toggleModel = (username) => {
    setSelectedModels(prev => {
      const next = new Set(prev);
      if (next.has(username)) next.delete(username);
      else next.add(username);
      return next;
    });
  };
  const selectAllModels = () => setSelectedModels(new Set(models.map(m => m.username)));
  const clearAllModels = () => setSelectedModels(new Set());

  // Estimate total channels based on selected models and tier filter
  const estimatedChannels = models
    .filter(m => selectedModels.has(m.username))
    .reduce((acc, m) => acc + (tierFilter === "both" ? (m.free + m.vip) : m[tierFilter] || 0), 0);

  const handleSendClick = () => {
    const hasText = !!caption.trim();
    if (selectedModels.size === 0) { toast.error("Seleccioná al menos una modelo"); return; }
    // Each selected model must have either media assigned OR caption is non-empty
    if (!hasText) {
      const missing = Array.from(selectedModels).filter(u => !assignments[u]);
      if (missing.length > 0) {
        toast.error(`Sin caption: ${missing.length} modelo(s) sin foto asignada. Asigná foto o escribí un mensaje.`);
        return;
      }
    }
    setConfirming(true);
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      // Build assignments map: { username: mediaId | null }
      const assignmentsMap = {};
      for (const u of selectedModels) {
        assignmentsMap[u] = assignments[u]?.id || null;
      }
      const r = await fetch("/api/admin/autopost/broadcast/send", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          caption,
          creatorUsernames: Array.from(selectedModels),
          tierFilter,
          assignments: assignmentsMap,
          stripLinks,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(`En cola: ${d.channelCount} canales (~${d.estimatedSeconds}s)`);
      setConfirming(false);
      setActiveBroadcastId(d.id);
      setCaption("");
      setAssignments({});
      setSelectedModels(new Set());
      loadHistory();
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  const openMediaPickerForModel = (username) => {
    setMediaModalTarget(username);
    setMediaFilterCreator(username); // pre-filter the picker to her vault
    setMediaModalOpen(true);
  };

  const assignMediaToModel = (modelUsername, mediaObj) => {
    setAssignments(prev => ({ ...prev, [modelUsername]: mediaObj }));
    setMediaModalOpen(false);
  };

  const removeMediaFromModel = (username) => {
    setAssignments(prev => {
      const next = { ...prev };
      delete next[username];
      return next;
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="autopost-broadcast">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 text-white/40 text-xs mb-1">
            <Link to="/admin/auto-posting" className="hover:text-white/70 inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Auto-Posting
            </Link>
            <span>/</span>
            <span>Broadcast</span>
          </div>
          <h1 className="text-2xl font-bold text-white inline-flex items-center gap-2">
            <Radio className="w-6 h-6 text-pink-400" /> Broadcast a Canales
          </h1>
          <p className="text-white/50 text-sm mt-1">Mensaje general + media → a todos los canales de las modelos seleccionadas.</p>
        </div>

        {/* ===== Step 1: Caption ===== */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <label className="text-[11px] uppercase tracking-wider text-white/50 mb-1 block">
            1. Mensaje <span className="text-white/30">({caption.length}/1024)</span>
          </label>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 1024))}
            placeholder="Ej: sexting hoy 8pm 🔥 // promo // saludos del finde"
            rows={3}
            data-testid="caption-input"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm resize-y"
          />
          <label className="flex items-center gap-2 text-xs text-white/55 mt-2">
            <input
              type="checkbox"
              checked={stripLinks}
              onChange={(e) => setStripLinks(e.target.checked)}
              data-testid="strip-links"
            />
            Quitar URLs antes de enviar (recomendado en canales free)
          </label>
        </div>

        {/* ===== Step 2: Models + Tier filter ===== */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold inline-flex items-center gap-2">
              <Users className="w-4 h-4" /> 2. Modelos
              <span className="text-white/40 text-xs">({selectedModels.size} / {models.length})</span>
            </h3>
            <div className="flex gap-1.5">
              <button onClick={selectAllModels} data-testid="select-all-models" className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10">Todas</button>
              <button onClick={clearAllModels} className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10">Ninguna</button>
              <button onClick={loadModels} disabled={loadingModels} className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10">
                <RefreshCw className={`w-3 h-3 ${loadingModels ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>

          {/* Tier filter */}
          <div className="flex items-center gap-2 mb-3 text-xs">
            <span className="text-white/40">Enviar a tier:</span>
            {[
              { key: "both", label: "Free + VIP" },
              { key: "free", label: "Solo Free" },
              { key: "vip", label: "Solo VIP" },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTierFilter(t.key)}
                data-testid={`tier-${t.key}`}
                className={`px-2.5 py-1 rounded-md text-xs font-medium border ${
                  tierFilter === t.key
                    ? "bg-pink-500/20 border-pink-400/50 text-pink-200"
                    : "bg-white/5 border-white/10 text-white/55 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loadingModels ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-white/40" /></div>
          ) : models.length === 0 ? (
            <div className="py-8 text-center">
              <AlertCircle className="w-6 h-6 text-amber-400 inline mb-2" />
              <p className="text-sm text-white/50">No hay modelos con canales configurados</p>
              <Link to="/admin/auto-posting" className="text-xs text-pink-300 underline mt-1 inline-block">
                Configurar canales →
              </Link>
            </div>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-96 overflow-y-auto pr-1">
              {models.map(m => {
                const checked = selectedModels.has(m.username);
                const channelCount = tierFilter === "both" ? (m.free + m.vip) : m[tierFilter] || 0;
                const assigned = assignments[m.username];
                return (
                  <li
                    key={m.username}
                    data-testid={`model-row-${m.username}`}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg transition ${
                      checked ? "bg-pink-500/15 border border-pink-400/40" : "bg-black/30 border border-white/[0.06] hover:bg-white/[0.04]"
                    }`}
                  >
                    <div
                      onClick={() => toggleModel(m.username)}
                      className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer"
                    >
                      <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                        checked ? "bg-pink-500 border-pink-400" : "border-white/30"
                      }`}>
                        {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                      </div>
                      {m.avatar ? (
                        <img src={m.avatar} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                          {(m.name || m.username).charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{m.name}</p>
                        <p className="text-[10px] text-white/45 truncate">
                          @{m.username} · {channelCount} canal{channelCount === 1 ? "" : "es"}
                        </p>
                      </div>
                    </div>
                    {/* Per-model media slot — only visible when this model is selected */}
                    {checked && (
                      assigned ? (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <img
                            src={assigned.thumbnailUrl || assigned.mediaUrl}
                            alt=""
                            className="w-10 h-10 rounded-md object-cover border border-pink-400/50"
                          />
                          <button
                            onClick={(e) => { e.stopPropagation(); openMediaPickerForModel(m.username); }}
                            data-testid={`change-media-${m.username}`}
                            className="text-[10px] px-1.5 py-1 rounded bg-white/5 hover:bg-white/15"
                            title="Cambiar"
                          >
                            ↻
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); removeMediaFromModel(m.username); }}
                            data-testid={`remove-media-${m.username}`}
                            className="text-[10px] px-1.5 py-1 rounded bg-red-500/15 hover:bg-red-500/30 text-red-200"
                            title="Quitar"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); openMediaPickerForModel(m.username); }}
                          data-testid={`assign-media-${m.username}`}
                          className="text-[10px] px-2 py-1.5 rounded-md bg-pink-500/15 border border-pink-400/30 text-pink-200 hover:bg-pink-500/25 flex-shrink-0 inline-flex items-center gap-1"
                        >
                          <ImageIcon className="w-3 h-3" /> Asignar
                        </button>
                      )
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ===== Step 3: Send ===== */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <p className="text-xs text-white/60 mb-3">
            <strong className="text-white">Tip:</strong> Si una modelo seleccionada no tiene foto asignada, se le envía solo el texto del mensaje. Si tampoco hay texto, se omite.
          </p>
          <button
            onClick={handleSendClick}
            disabled={selectedModels.size === 0 || sending || (!caption.trim() && Object.keys(assignments).length === 0)}
            data-testid="send-broadcast-btn"
            className="w-full py-3 rounded-xl font-bold bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            Revisar y enviar a {selectedModels.size} modelo{selectedModels.size === 1 ? "" : "s"} (≈{estimatedChannels} canal{estimatedChannels === 1 ? "" : "es"})
          </button>
        </div>

        {/* ===== History ===== */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <h3 className="font-bold mb-3 inline-flex items-center gap-2">
            <History className="w-4 h-4" /> Últimos broadcasts
          </h3>
          {history.length === 0 ? (
            <p className="text-sm text-white/40">Sin envíos todavía.</p>
          ) : (
            <ul className="space-y-1.5">
              {history.map(h => {
                const total = h.channelCount || 0;
                const sent = h.sentCount || 0;
                const failed = h.failedCount || 0;
                const pct = total ? Math.round(((sent + failed) / total) * 100) : 0;
                const isLive = h.status === "in_progress" || h.status === "queued";
                return (
                  <li key={h.id} data-testid={`history-${h.id}`} className={`p-3 rounded-lg border ${isLive ? "bg-pink-500/5 border-pink-500/30" : "bg-black/30 border-white/[0.06]"}`}>
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        {h.thumbnailUrl && (
                          <img src={h.thumbnailUrl} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{h.caption || "(sin caption)"}</p>
                          <p className="text-[10px] text-white/40">{formatDate(h.createdAt)} · por {h.createdBy}</p>
                        </div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${
                        h.status === "done" ? "bg-emerald-500/20 text-emerald-300" :
                        h.status === "in_progress" ? "bg-pink-500/20 text-pink-300 animate-pulse" :
                        h.status === "queued" ? "bg-white/10 text-white/60" :
                        "bg-red-500/20 text-red-300"
                      }`}>
                        {h.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-[11px]">
                      <span className="text-white/50">Canales: <strong className="text-white">{total}</strong></span>
                      <span className="text-emerald-300">✓ {sent}</span>
                      {failed > 0 && <span className="text-red-300">✗ {failed}</span>}
                      {isLive && (
                        <span className="ml-auto flex items-center gap-1.5 text-pink-300">
                          <Loader2 className="w-3 h-3 animate-spin" /> {pct}%
                        </span>
                      )}
                    </div>
                    {isLive && (
                      <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-pink-500 to-rose-500 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ===== Media picker modal ===== */}
        {mediaModalOpen && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setMediaModalOpen(false)}>
            <div className="bg-zinc-950 border border-white/10 rounded-2xl p-5 max-w-4xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="media-modal">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-lg inline-flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  {mediaModalTarget ? (
                    <>Asignar foto a <span className="text-pink-300">@{mediaModalTarget}</span></>
                  ) : (
                    <>Elegí el media</>
                  )}
                </h3>
                <button onClick={() => setMediaModalOpen(false)} className="text-white/50 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mb-3 text-xs">
                <select
                  value={mediaFilterCreator}
                  onChange={(e) => setMediaFilterCreator(e.target.value)}
                  className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5"
                >
                  <option value="">Todas las modelos</option>
                  {models.map(m => <option key={m.username} value={m.username}>{m.name}</option>)}
                </select>
                <select
                  value={mediaFilterType}
                  onChange={(e) => setMediaFilterType(e.target.value)}
                  className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5"
                >
                  <option value="photo">📷 Solo fotos</option>
                  <option value="video">🎥 Solo videos</option>
                  <option value="">Todos</option>
                </select>
                <span className="ml-auto self-center text-white/40">{media.length} items</span>
              </div>
              <div className="flex-1 overflow-y-auto pr-1">
                {loadingMedia ? (
                  <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-white/40" /></div>
                ) : media.length === 0 ? (
                  <p className="text-sm text-white/40 py-6 text-center">Sin media en la bóveda con esos filtros</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                    {media.map(m => (
                      <button
                        key={m.id}
                        onClick={() => assignMediaToModel(mediaModalTarget, m)}
                        data-testid={`media-pick-${m.id}`}
                        className="relative aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-pink-400 transition group"
                      >
                        {m.mediaType === "video" ? (
                          <video src={m.mediaUrl} poster={m.thumbnailUrl} className="w-full h-full object-cover" muted />
                        ) : (
                          <img src={m.thumbnailUrl || m.mediaUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
                        )}
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-1 text-[9px] text-white truncate">
                          @{m.creatorUsername}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== Confirm modal ===== */}
        {confirming && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !sending && setConfirming(false)}>
            <div className="bg-zinc-950 border border-pink-500/30 rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="confirm-modal">
              <h3 className="font-black text-lg mb-3">Confirmar broadcast</h3>
              {/* Per-model assignments summary */}
              <div className="max-h-48 overflow-y-auto bg-black/40 rounded-lg border border-white/10 mb-3">
                <ul className="divide-y divide-white/[0.06]">
                  {Array.from(selectedModels).map(u => {
                    const a = assignments[u];
                    return (
                      <li key={u} className="px-3 py-2 flex items-center gap-2 text-xs">
                        <span className="font-mono text-pink-200 flex-1">@{u}</span>
                        {a ? (
                          <>
                            <img src={a.thumbnailUrl || a.mediaUrl} alt="" className="w-7 h-7 rounded object-cover" />
                            <span className="text-white/40">{a.mediaType}</span>
                          </>
                        ) : (
                          <span className="text-amber-200">solo texto</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
              {caption && (
                <div className="bg-black/40 rounded-lg p-3 border border-white/10 mb-3 max-h-24 overflow-y-auto">
                  <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Mensaje</p>
                  <p className="text-sm whitespace-pre-wrap">{caption}</p>
                </div>
              )}
              <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-3 mb-4">
                <p className="text-sm text-pink-100">
                  Se enviará a <strong className="text-pink-300">{selectedModels.size} modelo{selectedModels.size === 1 ? "" : "s"}</strong>
                  {" "}(≈ {estimatedChannels} canal{estimatedChannels === 1 ? "" : "es"}, tier {tierFilter})
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setConfirming(false)} disabled={sending} className="flex-1 py-2.5 rounded-lg bg-white/5 border border-white/15 hover:bg-white/10 font-semibold text-sm">
                  Cancelar
                </button>
                <button
                  onClick={confirmSend}
                  disabled={sending}
                  data-testid="confirm-send-btn"
                  className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Encolando…</> : <><Send className="w-4 h-4" /> Enviar ahora</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
